"use client";

import { useState } from "react";
import Modal from "..";
import { Button } from "@/components/ui/button";
import { Controller, useForm } from "react-hook-form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import moment from "moment";
import { Check, Loader } from "lucide-react";
import { toast } from "sonner";
import { useGetUpcomingBatchesQuery } from "@/features/cms/cmsApi";
import { QuestionCircle } from "@/components/icons";

const price = 549;

const TrainingSlot = ({ children, className, batches = [] }: any) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: upcomingBatchesResponse } = useGetUpcomingBatchesQuery(
    {},
    {
      skip: batches.length > 0,
    }
  );

  const upcomingBatches = upcomingBatchesResponse?.data?.batches || batches;
  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
    reset,
  } = useForm({
    defaultValues: {
      trainingSlot: "",
      numberOfTrainees: undefined,
    },
    mode: "onChange",
  });

  const numberOfTrainees = watch("numberOfTrainees") || 0;
  const selectedTrainingSlot = watch("trainingSlot");

  const onSubmit = async (data: any) => {
    if (step === 1) {
      setStep(2);
      return;
    }

    setIsSubmitting(true);
    // setError(null);

    try {
      // Safely construct URL
      const baseUrl = process.env.NEXT_PUBLIC_ADMIN_PANEL_BASE_URL;

      const params = new URLSearchParams({
        batch_id: data.trainingSlot,
        trainee_count: data.numberOfTrainees.toString(),
      });

      const redirectUrl = `${baseUrl}/register?${params.toString()}`;

      // Brief delay for smoother UX (optional)
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Redirect (using .assign() to ensure history is kept)
      window.location.assign(redirectUrl);

      // Fallback cleanup (if redirect fails)
      setTimeout(() => {
        setOpen(false);
        setStep(1);
        reset();
      }, 1000);
    } catch (err) {
      toast.error("Redirect error:", err || "Redirection failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        className={cn(
          "flex cursor-pointer items-center justify-center",
          className
        )}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>

      <Modal open={open} onOpenChange={setOpen}>
        <div className="flex items-center justify-center gap-2 lg:gap-4">
          {/* Step 1 Indicator */}
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "flex size-6 items-center justify-center rounded-full lg:size-8",
                step >= 1
                  ? "border-blue-600 bg-blue-600"
                  : "border-2 border-slate-300 bg-transparent"
              )}
            >
              {step > 1 ? (
                <Check className="size-3.5 text-white lg:size-5" />
              ) : (
                <div className="size-2.5 rounded-full bg-white lg:size-3"></div>
              )}
            </div>
            <span
              className={cn(
                "text-sm font-semibold lg:text-base",
                step >= 1 ? "text-blue-600" : "text-slate-500"
              )}
            >
              Select Session
            </span>
          </div>

          {/* Connector Line */}
          <div
            className={cn(
              "h-0.5 w-8 lg:w-12",
              step >= 2 ? "bg-blue-600" : "bg-slate-300"
            )}
          />

          {/* Step 2 Indicator */}
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "flex size-6 items-center justify-center rounded-full lg:size-8",
                step >= 2
                  ? "border-blue-600 bg-blue-600"
                  : "border-2 border-slate-300 bg-transparent"
              )}
            >
              <div
                className={cn(
                  "size-2.5 rounded-full lg:size-3",
                  step >= 2 ? "bg-white" : "bg-slate-300"
                )}
              ></div>
            </div>
            <span
              className={cn(
                "text-sm font-semibold lg:text-base",
                step >= 2 ? "text-blue-600" : "text-slate-500"
              )}
            >
              Number of trainee
            </span>
          </div>
        </div>

        {step === 1 && (
          <form
            className="mt-2.5 flex flex-col gap-4 lg:gap-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-3">
              <h3 className="text-primary text-xl font-semibold lg:text-4xl">
                Select your training start date
              </h3>
              <p className="text-base text-slate-600 lg:text-xl">
                Pick the start date that works best for your team. We run fresh
                sessions every month.
              </p>
            </div>
            {!upcomingBatches.length && (
              <div className="text-primary border border-slate-100 px-6 py-3 text-center text-lg font-medium lg:py-4 lg:text-xl">
                No Upcoming Sessions
              </div>
            )}
            <Controller
              name="trainingSlot"
              control={control}
              rules={{ required: "Please select a training slot" }}
              render={({ field, fieldState: { error } }) => (
                <div className="space-y-2">
                  <RadioGroup
                    className="max-h-[300px] gap-0 overflow-y-auto lg:max-h-[400px]"
                    value={field.value}
                    onValueChange={field.onChange}
                    aria-label="Training slots"
                  >
                    {upcomingBatches.map((slot: any) => (
                      <Label
                        key={slot.id}
                        htmlFor={String(slot.id)}
                        className="flex items-center space-x-2 border-x border-t border-slate-300 px-6 py-4 transition-colors last:border-b hover:bg-slate-50"
                      >
                        <RadioGroupItem
                          value={slot.id}
                          id={String(slot.id)}
                          indicatorClassName="fill-black h-[14px] w-[14px] stroke-none"
                          className="h-5 w-5 border border-black"
                        />
                        <span className="cursor-pointer text-sm font-medium text-slate-800 lg:text-xl">
                          {moment(slot.start_date).format("MMMM DD, YYYY")} -{" "}
                          {moment(slot.end_date).format("MMMM DD, YYYY")}
                        </span>
                      </Label>
                    ))}
                  </RadioGroup>
                  {error && (
                    <p className="text-sm text-red-500">{error.message}</p>
                  )}
                </div>
              )}
            />

            <div className="mt-4 flex w-full justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                size="xl"
                className="w-1/2 rounded-full"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="xl"
                className="w-1/2 rounded-full"
                disabled={!selectedTrainingSlot}
              >
                Continue
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form
            className="mt-2.5 flex flex-col gap-4 lg:gap-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-3">
              <h3 className="text-primary text-2xl font-semibold lg:text-4xl">
                Add Trainees & Review Cost ${price}/person
              </h3>
              <p className="text-base text-slate-600 lg:text-xl">
                Enter how many trainees you&apos;re enrolling to calculate your
                total fee.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="numberOfTrainees">
                Number of Trainees <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="numberOfTrainees"
                control={control}
                rules={{
                  required: "Number of trainees is required",
                  min: {
                    value: 1,
                    message: "Must be at least 1 trainee",
                  },
                  max: {
                    value: 10000,
                    message: "Must be at most 10000 trainees",
                  },
                  validate: (value) =>
                    Number.isInteger(Number(value)) || "Must be a whole number",
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <div className="space-y-1">
                    <Input
                      id="numberOfTrainees"
                      type="number"
                      min="1"
                      max="10000"
                      step="1"
                      placeholder="Enter number of trainees"
                      className="rounded-lg"
                      value={value ?? ""}
                      onChange={(e) =>
                        onChange(
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value)
                        )
                      }
                      errorText={error?.message}
                    />
                  </div>
                )}
              />
            </div>

            {numberOfTrainees > 0 && isValid && (
              <div className="flex items-center justify-between rounded-lg border border-amber-500 bg-amber-50 px-4 py-2.5 text-xl font-semibold text-amber-500 lg:text-3xl">
                <div>
                  {numberOfTrainees} × ${price}
                </div>
                <div>= ${price * numberOfTrainees}</div>
              </div>
            )}
            {/* Information Collection Redirection Notice */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <QuestionCircle className="mt-0.5 shrink-0 text-blue-600" />

              <p className="text-sm text-blue-600">
                We&apos;ll redirect you
                {process.env.NEXT_PUBLIC_ADMIN_PANEL_BASE_URL &&
                  ` to ${
                    new URL(process.env.NEXT_PUBLIC_ADMIN_PANEL_BASE_URL)
                      .hostname
                  }`}{" "}
                to securely complete your organization registration.
              </p>
            </div>
            <div className="mt-4 flex w-full justify-between gap-4">
              <Button
                type="button"
                variant="outline"
                size="xl"
                className="w-1/2 rounded-full"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                type="submit"
                size="xl"
                className="w-1/2 rounded-full"
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader className="animate-spin" /> Redirecting...
                  </span>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default TrainingSlot;
